import { useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import {
  ahuBoardApi,
  useGetAhuBoardUserQuery,
  useGetAhuBoardPermissionsQuery,
  useGetAhuUnitQuery,
  useGetAhuTunnelListQuery,
  useGetAhuSectionListQuery,
  useGetCmsAhuDocQuery,
  useGetAhuBoardProjectQuery,
  useGetConstructionClassesQuery,
} from 'app/services/ahuBoardApi';
import { getBootUserId } from 'hooks/useDashboardData';
import type {
  IAhuBoardSection,
  IAhuBoardTunnel,
  IAhuBoardUnit,
  IAhuBoardProject,
  IAhuDmountRow,
  IAhuAccessoryRow,
  IAhuAccessoryDetail,
  IAhuPositionRow,
  IAhuIntWallRow,
  IAhuTunnelLugPlacement,
  IAhuCoilDetail,
  IAhuRatedCoil,
  IAhuEvapPackDetail,
  IAllPermissions,
  IBoardUser,
  ICmsAhuDoc,
  IConstructionClass,
} from 'models';
import {
  DRAWABLE_ACCESSORY_TYPES,
  accessoryTypeNeedsPosition,
} from '../three/bootDrawFeeds';
import { lugsOnForTunnel } from '../hybridLugFields';
import { hasHybridTunnelShape, isHybridAhu } from '../addressing';

/**
 * Per-section component facts the CANVAS consumes (G33 re-homes of the
 * stateStorage component-model probes — canvas.view3d:5384/:5459). The legacy
 * board loads every section's component list + concrete models through the
 * tab.section layer at boot; the canvas then reads duty/orientation out of
 * stateStorage. Here the same §20.5 #43/#47 fetches are hoisted into the
 * bootstrap and reduced to the two canvas-read fields.
 */
export interface SectionComponentMeta {
  coil_duty_id?: number | null;
  fan_orientation_id?: number | null;
  /** getSectionNameColor UDC probe (canvas.view3d:5739) — udc_name_id truthiness. */
  udc_has_name?: boolean;
  /**
   * getSectionNameColor gas-heater probe — AHUGasHeater.model truthiness.
   * Dev colours a gas heater section off `required_length` alone, which is
   * null for direct fire because that type never persists a
   * master_gas_heater (see the PR body); `model` is the marker that covers
   * both types.
   */
  gh_has_model?: boolean;
}

/** G31 boot feed — coil detail + its RatedCoilModel row (§20.5 #47/#50). */
export interface SectionCoilFeed {
  coil: IAhuCoilDetail;
  rated: IAhuRatedCoil | null;
}

/** G12-G21 boot feed — one drawable accessory with its detail + position rows. */
export interface AccessoryBootRow {
  type: string;
  row: IAhuAccessoryRow;
  detail: IAhuAccessoryDetail | null;
  position: IAhuPositionRow | null;
}

export interface AhuBoardBootstrap {
  user: IBoardUser | undefined;
  ahu: IAhuBoardUnit | undefined;
  tunnels: IAhuBoardTunnel[] | undefined;
  /** null while the per-tunnel section fetches are in flight. */
  sectionsByTunnel: Record<number, IAhuBoardSection[]> | null;
  /**
   * §20.4 #38 — dmount rows per section (G29 input). null while in flight;
   * the canvas treats missing rows as "no dmounts yet" (legacy pre-tab state).
   */
  dmountsBySection: Record<number, IAhuDmountRow[]> | null;
  /**
   * §20.5 #43 + #47 — coil duty / fan orientation / UDC-name per section
   * (G33 + label-color input). null while in flight (legacy pre-fetch
   * stateStorage = empty → neutral arrows / no fan-tunnel arrow, same
   * first-paint semantics).
   */
  componentMetaBySection: Record<number, SectionComponentMeta> | null;
  /**
   * 6c2 — G31 coil-piping boot feed (§20.5 #50 rating_mode_coils per coil;
   * legacy: coil form init → draw:coil:connection). null while in flight.
   */
  coilFeedsBySection: Record<number, SectionCoilFeed> | null;
  /**
   * 6c2 — G32 evap-piping boot feed (§20.5 #47 evap_packs detail; legacy:
   * evap form drawConnections). null while in flight.
   */
  evapFeedsBySection: Record<number, IAhuEvapPackDetail> | null;
  /**
   * 6c2 — G11 boot input (§20.4 #31/#34 per section, plane-stamped; legacy:
   * tab.section int-wall collections → storage poll → draw events).
   */
  intWallsBySection: Record<number, IAhuIntWallRow[]> | null;
  /**
   * 6c2 — G12-G21 boot input (§20.6 #51 rows + #54 details + #59 positions
   * for the drawable types; legacy: every accessory form's boot render →
   * drawOnCanvas). null while in flight.
   */
  accessoriesBootBySection: Record<number, AccessoryBootRow[]> | null;
  /**
   * 6c2 — G7-G10 LugCollection re-home (§20.7 #63 per base_id tunnel; legacy:
   * tab.lifting.lug init fetches then fires redraw:canvas). null while in
   * flight = legacy pre-fetch state (no lugs drawn yet).
   */
  lugsByTunnel: Record<number, IAhuTunnelLugPlacement> | null;
  /**
   * 6c2 — isCube plane-1 probe input (`tunnel.floor.under_insulation_label`,
   * canvas.view3d:1480; ahu/floors/<floor_id> per floor_id tunnel).
   */
  floorLabelByTunnel: Record<number, unknown> | null;
  project: IAhuBoardProject | undefined;
  permissions: IAllPermissions | undefined;
  constructionClasses: IConstructionClass[] | undefined;
  cms: ICmsAhuDoc | undefined;
  /** Shell-ready: legacy MainView constructs only after ahu + tunnels resolve. */
  shellReady: boolean;
  /**
   * The canvas' FIRST-draw gate: every tunnel that the server says has a length
   * has its section rows on this side, or the re-reads for it are spent. See
   * the note at the value itself for why a non-null `sectionsByTunnel` is not
   * the same question — and note it is **latched**: once true for this AHU it
   * stays true, because only the first draw asks it.
   */
  sectionsComplete: boolean;
  /** Does this unit have the hybrid flag, or a (row, level) cell with two tunnels in it? */
  isHybridUnit: boolean;
  ahuError: boolean;
}

/** Stable empty array — keeps the gap selector's identity stable. */
const EMPTY_IDS: number[] = [];
/** Re-reads for a section list that failed or came back short, and their step. */
const SECTION_LIST_RETRIES = 3;
const SECTION_LIST_RETRY_MS = 600;

/**
 * Keep the last non-null `value` until `resetKey` changes.
 *
 * The board's feeds are `null` while loading (the boot-draw contract), but
 * they go back to null on every later section write too — a new tunnel's list
 * or a new section's dmounts is briefly missing — and `canvasTunnels` turns
 * a null map into "no sections", so an explicit canvas pass in that window
 * drew an empty cabinet and then drew again once the data landed. After the
 * first load a feed is refreshed in place, never blanked.
 */
function useHeldValue<T>(value: T | null, resetKey: unknown): T | null {
  const held = useRef<{ key: unknown; value: T | null }>({ key: resetKey, value: null });
  if (held.current.key !== resetKey) held.current = { key: resetKey, value: null };
  if (value != null) held.current.value = value;
  return value ?? held.current.value;
}

/**
 * Latch `value` once it has been true, until `resetKey` changes — same shape
 * and the same reason as `useHeldValue`.
 *
 * Only the FIRST draw asks whether the data is consistent. Afterwards the same
 * question gates the data-change redraws, the boot-draw replay and
 * `boardResolved`, and a gate that can dip back to false there would stop the
 * canvas redrawing from data for the rest of the session — including the user's
 * edits to tunnels that are perfectly loaded. The pre-#2133 gate could not dip
 * (`sectionsByTunnel` is itself held), so the latch is what keeps that true.
 */
function useLatched(value: boolean, resetKey: unknown): boolean {
  const latched = useRef<{ key: unknown; value: boolean }>({ key: resetKey, value: false });
  if (latched.current.key !== resetKey) latched.current = { key: resetKey, value: false };
  if (value) latched.current.value = true;
  return latched.current.value;
}

/**
 * Bootstrap fetch graph (§20.1 — fired on every board load), re-homed from the
 * fn-map promise web (§1.5/§1.6):
 *
 *   #1 user (body[data-user_id])      → mfg_id for the CMS doc key
 *   #3 permissions                    → body permission classes (parity 10)
 *   #4 ahu unit                       → project_id, construction_class_id
 *   #5 tunnels (sorted by index_id)
 *   #6 sections per tunnel            → PARALLEL (deliberate dedrift, §26.1:
 *                                       legacy fetched sequentially only as a
 *                                       recalc-order artifact)
 *   #7 CMS doc (mfg + construction class keys)
 *   #8 project                        → document.title + permission downgrade
 *   #9 construction classes           → HOISTED here per risk R15 (canvas
 *                                       Y-offset source must not race tab.ahu)
 *
 * §20.1 #10 (per-tunnel validation) is deliberately NOT kicked in 6a — the
 * error manager owns it (6e); §20.1 #2 (mfg) is covered by the server-injected
 * __KSDM_CONFIG__.mfg the shared header already consumes.
 */
export function useAhuBoardBootstrap(ahuId: number | null): AhuBoardBootstrap {
  const dispatch = useAppDispatch();
  const bootUserId = getBootUserId();

  const { data: user, isError: userError } = useGetAhuBoardUserQuery(
    bootUserId ?? skipToken
  );
  const {
    data: ahu,
    isError: ahuError,
  } = useGetAhuUnitQuery(ahuId ?? skipToken);
  const { data: tunnels } = useGetAhuTunnelListQuery(ahuId ?? skipToken);
  const { data: permissionsList } = useGetAhuBoardPermissionsQuery();
  const { data: constructionClasses } = useGetConstructionClassesQuery();
  const { data: project } = useGetAhuBoardProjectQuery(
    ahu?.project_id ?? skipToken
  );

  const releasedFeedsForRef = useRef<number | null>(null);
  const mfgId = user?.mfg_id;
  const constructionClassId = ahu?.construction_class_id;
  const { data: cms, isError: cmsError } = useGetCmsAhuDocQuery(
    mfgId != null && constructionClassId != null
      ? { mfgId, constructionClassId }
      : skipToken
  );

  /**
   * HYBRID AHUs ONLY — hold the non-critical per-section feeds until the
   * canvas' own gate can close (reported 2026-10-19). Internal to this hook:
   * nothing outside it needs to know, the three effects below are the only
   * readers.
   *
   * The first draw needs two things and nothing else: the section rows, and
   * the CMS doc (`AhuBoardPage`: `sectionsReady = sectionsComplete && cms !=
   * null`). The CMS doc is a 40 ms request, but it can only be ASKED FOR once
   * the slow `GET ahu/units/<id>` has landed — and by then the three feed
   * effects below have already put ~90 per-section requests on the wire. With
   * four gunicorn workers that burst takes ~15 s to drain and the CMS doc
   * queues behind all of it, so the canvas sat behind its preloader for 26 s
   * instead of 11 s. Nothing about the burst is a draw input: the dmount,
   * component-meta, int-wall, accessory, lug and floor feeds are all allowed
   * to land on an already-revealed canvas (see AhuCanvas' boot-paint note), so
   * waiting one short request for them costs nothing. Measured over three
   * loads each: box 7177 time-to-canvas 9.3 s -> 7.4 s, box 7172 (28 tunnels)
   * 11.9 s -> 8.0 s.
   *
   * The unit's single validation request is deliberately NOT held with them:
   * holding it too left time-to-canvas unchanged (7.2 s either way -- one
   * request on one of four workers starves nothing) and pushed the whole load
   * 1.5 s later, because it then ran in the busiest part of it rather than the
   * quietest.
   *
   * Only on a hybrid unit, where the burst is 20-30 tunnels' worth. A standard
   * unit's is a handful of requests that never starved anything, and this stays
   * `false` for it — same effects, same order, same timing as before.
   *
   * The hold has to start BEFORE the unit GET lands, not when the CMS doc
   * becomes askable: the section rows arrive first, and the burst they trigger
   * is already on the wire by then. So it holds while the doc is merely still
   * POSSIBLE, and releases the moment it is not. Every way it can become
   * impossible has to be in `cmsCannotArrive`, including the ones that leave
   * its query on `skipToken` — a skipped query never errors, so a failed user
   * fetch or a missing boot user id would otherwise hold the feeds for the
   * whole session. A board whose CMS doc never comes cannot draw anyway, and
   * must still fill its left panel.
   */
  const isHybridUnit = isHybridAhu(ahu, tunnels);
  const cmsCannotArrive =
    cmsError ||
    ahuError ||
    userError ||
    bootUserId == null ||
    (ahu != null && constructionClassId == null) ||
    (user != null && mfgId == null);
  // ONE-WAY. A unit that is hybrid by FLAG only -- one tunnel, so no shared
  // cell -- reads as standard until the unit GET lands, by which time the
  // feeds have started. Letting the hold switch on then would cancel them and
  // run them again, and `getAhuCoilDetail` / `getAhuGasHeaterDetail` carry
  // `forceRefetch: true`, so those would really go out twice (and a coil GET
  // writes). Holding is only ever worth anything BEFORE they start, so once
  // they are away this stays released for the rest of the AHU. Latched per
  // `ahuId`, during render rather than in an effect, because a render that
  // released them has already let them go.
  const hold = isHybridUnit && cms == null && !cmsCannotArrive;
  if (!hold && tunnels != null) releasedFeedsForRef.current = ahuId;
  const holdNonCriticalFeeds = hold && releasedFeedsForRef.current !== ahuId;

  /**
   * HYBRID AHUs ONLY — read the whole unit's sections with ONE
   * `GET ahu/sections/?ahu_id=` instead of one `?tunnel_id=` per tunnel, and
   * seed the per-tunnel cache entries from it (reported 2026-10-19).
   *
   * A hybrid unit is one tunnel per section, so §20.1 #6 is a request per
   * SECTION: 11 on the box's biggest fixture, 20-30 on the units QA builds.
   * They all run the same serializer over the same rows, so the single request
   * does the same total server work in one worker slot instead of N, and
   * `useAhuBoardSyncdata`/the canvas get the rows in one store write.
   *
   * SHAPE, not the flag. `hasHybridTunnelShape` answers from the tunnel rows,
   * which is what the fan-out below already waits for; `isHybridAhu` would add
   * the slow `GET ahu/units/<id>` to the critical path of the board's FIRST
   * draw input (`sectionsComplete`), which is exactly the regression the
   * `holdNonCriticalFeeds` note above exists to avoid. The only unit the two
   * disagree about is hybrid by flag with a single tunnel — one request either
   * way, so nothing is lost by treating it as standard here.
   *
   * Latched per `ahuId` during render, same reason as `releasedFeedsForRef`:
   * adding a tunnel can flip a standard unit's shape to hybrid mid-session,
   * and switching modes then would re-read every settled list.
   */
  const singleSectionFetchRef = useRef<{ ahuId: number | null; on: boolean }>({
    ahuId: null,
    on: false,
  });
  if (tunnels != null && singleSectionFetchRef.current.ahuId !== ahuId) {
    singleSectionFetchRef.current = { ahuId, on: hasHybridTunnelShape(tunnels) };
  }
  const singleSectionFetch =
    ahuId != null &&
    singleSectionFetchRef.current.ahuId === ahuId &&
    singleSectionFetchRef.current.on;

  const { data: ahuSectionRows, isError: ahuSectionsError } = useGetAhuSectionListQuery(
    singleSectionFetch && ahuId != null ? ahuId : skipToken
  );

  // Fan the one response out into the per-tunnel entries every other reader
  // and writer on this board already uses, so NOTHING else changes shape.
  // `upsertQueryData` runs the normal query lifecycle with a forced result, so
  // the entries land `fulfilled` and provide their `{ AhuSections, <id> }`
  // tags — but it skips `transformResponse`, so the `order_rate` sort that
  // endpoint does has to be applied here (§21.3).
  //
  // ONCE per AHU. The effect re-runs on a new `tunnels` identity (a tunnel
  // added after boot), and re-seeding then would overwrite every tunnel's live
  // rows with the boot snapshot.
  const [seededAhuId, setSeededAhuId] = useState<number | null>(null);
  const seedStartedForRef = useRef<number | null>(null);
  useEffect(() => {
    if (!singleSectionFetch || ahuId == null || !tunnels || tunnels.length === 0) return;
    if (ahuSectionRows == null) return;
    if (seedStartedForRef.current === ahuId) return;
    seedStartedForRef.current = ahuId;

    // Every tunnel of the unit gets an entry, including the ones with no
    // sections: the live selector below reports the map as null until each
    // tunnel has one, and an empty hybrid tunnel is an ordinary state
    // (a section can be added to it, and #1811 leaves zero-section tunnels
    // behind on the AHU's last delete).
    const byTunnel = new Map<number, IAhuBoardSection[]>();
    tunnels.forEach((t) => byTunnel.set(t.id, []));
    ahuSectionRows.forEach((row) => {
      const rows = byTunnel.get(row.tunnel_id);
      // A tunnel the (cached) tunnel list has not caught up with still gets
      // seeded — the fan-out subscribes to it when the list does land, and
      // finds the rows already there.
      if (rows) rows.push(row);
      else byTunnel.set(row.tunnel_id, [row]);
    });

    // Not cancelled on cleanup: the fan-out below is gated on this landing, so
    // an effect re-run that dropped the in-flight seed (the ref above lets
    // only one through) would hold the board's sections for the session.
    // `setSeededAhuId` is compared against the CURRENT `ahuId`, so a late one
    // from a board that has moved on gates nothing.
    void Promise.all(
      [...byTunnel].map(([tunnelId, rows]) =>
        dispatch(
          ahuBoardApi.util.upsertQueryData(
            'getTunnelSectionList',
            tunnelId,
            [...rows].sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0))
          )
        )
      )
    ).then(() => {
      // The fan-out below must not subscribe before the entries exist, or
      // `initiate` finds nothing cached and sends the per-tunnel GETs after
      // all. Flipping this is what releases it.
      setSeededAhuId(ahuId);
    });
  }, [singleSectionFetch, ahuSectionRows, tunnels, ahuId, dispatch]);

  // ---- §20.1 #6: one GET ahu/sections/?tunnel_id= per tunnel via the RTKQ
  // cache (same `initiate` pattern as project-board useAhuSections). The
  // effect only owns the fetch subscriptions; the DATA is read LIVE from the
  // cache below so later writes (createAhuSection onQueryStarted push, the
  // duplicate-section poll's forceRefetch, ConfigureTunnel edits) reach the
  // board without a reload. The previous one-shot useState snapshot never saw
  // those writes — new sections stayed invisible until reload (QA batch 4
  // #1/#3; legacy's Backbone collections are live by construction). ----
  //
  // On a hybrid unit the entries are already `fulfilled` from the seed above
  // by the time this runs, and `initiate` without `forceRefetch` does not
  // re-read a fulfilled entry — so this keeps owning the SUBSCRIPTIONS
  // (cache lifetime, and the re-read paths that invalidate or force) without
  // putting a second request on the wire. A tunnel added later has no entry
  // and is fetched on its own, as before. If the one request FAILED there is
  // nothing to subscribe to, so the per-tunnel fan-out runs after all rather
  // than leaving the board with no sections at all.
  const sectionSeedPending =
    singleSectionFetch && !ahuSectionsError && seededAhuId !== ahuId;
  useEffect(() => {
    if (!tunnels || ahuId == null || tunnels.length === 0) return undefined;
    if (sectionSeedPending) return undefined;
    const subs = tunnels.map((t) =>
      dispatch(ahuBoardApi.endpoints.getTunnelSectionList.initiate(t.id))
    );
    return () => {
      subs.forEach((s) => s.unsubscribe());
    };
  }, [tunnels, ahuId, dispatch, sectionSeedPending]);

  // Live cache read — null while any tunnel's list is still in flight (the
  // old snapshot semantics); an errored fetch degrades to [] for that tunnel.
  // shallowEqual keeps the map identity stable across unrelated store writes
  // (RTKQ row-array references only change when that list changes).
  const selectSectionsByTunnel = useMemo(
    () => (state: RootState): Record<number, IAhuBoardSection[]> | null => {
      if (!tunnels || ahuId == null) return null;
      const acc: Record<number, IAhuBoardSection[]> = {};
      for (const t of tunnels) {
        const q = ahuBoardApi.endpoints.getTunnelSectionList.select(t.id)(state);
        if (q.data) acc[t.id] = q.data;
        else if (q.isError) acc[t.id] = [];
        else return null;
      }
      return acc;
    },
    [tunnels, ahuId]
  );
  const sectionsLive = useAppSelector(selectSectionsByTunnel, shallowEqual);
  // A tunnel added after boot has no list in the cache for a tick, which turns
  // the whole map null. Hold the last map so the other tunnels keep their
  // sections (the new tunnel simply has none yet); `sectionsReady` only gates
  // the FIRST draw, which the held value cannot affect (null until first load).
  const sectionsByTunnel = useHeldValue(sectionsLive, ahuId);

  // ---- Tunnels whose section list needs re-reading (§20.1 #6 gaps) ----
  // Two kinds, both of which the live selector above hands the canvas as `[]`,
  // indistinguishable from "this tunnel has no sections" and neither of which
  // anything re-fetched:
  //
  //  - the list came back as an ERROR. One 502 or aborted GET in the board's
  //    per-tunnel fan-out left that tunnel missing from the 3D view until the
  //    page was reloaded — a blank canvas under a fully populated left panel
  //    when the whole fan-out failed.
  //  - the list is FULFILLED and empty while the tunnel's own row carries a
  //    length. `AHUTunnels.length` is the live sum of its sections'
  //    (models_tunnel.py), so the two disagree only on this side. It is what a
  //    second tab leaves behind: `useAhuBoardSyncdata` applies a `put` for a
  //    section but drops a `post`, so an insert made elsewhere reaches this tab
  //    as a new tunnel length with no rows behind it.
  //
  // Nothing else re-reads a settled list — the fan-out effect only re-runs on a
  // new tunnels identity, and there is no polling or refetchOnFocus.
  //
  // BOTH SIDES OF THE COMPARISON ARE READ FROM THE STORE, the tunnel rows
  // included — reading them off the `tunnels` prop instead compared a live
  // section list against a render-old tunnel list, and a section DELETE moves
  // the two within a tick of each other. Deleting a section emptied its list
  // (the mutation's optimistic filter) and zeroed its tunnel's length
  // (`recalcTunnelLength`) in the same dispatch sequence, and the server drops
  // a tunnel whose last section goes, so the next `tunnels/?ahu_id=` leaves it
  // out altogether — but the captured array still carried the tunnel at its
  // old length, which reads as "a tunnel with a length and no rows" and sent a
  // `GET ahu/sections/?tunnel_id=` for a row that no longer exists (404,
  // "Tunnel object doesn't exist"). On a hybrid unit that was EVERY section
  // delete, each tunnel there holding a single section (reported 2026-10-22).
  const selectSectionListGaps = useMemo(
    () =>
      (state: RootState): number[] => {
        if (ahuId == null) return EMPTY_IDS;
        const tunnelRows = ahuBoardApi.endpoints.getAhuTunnelList.select(ahuId)(state).data;
        if (!tunnelRows) return EMPTY_IDS;
        const gaps = tunnelRows
          .filter((t) => {
            const q = ahuBoardApi.endpoints.getTunnelSectionList.select(t.id)(state);
            if (q.isError) return true;
            // Only a settled-but-short list is a gap; one still in flight is
            // simply not here yet (the live selector reports the map as null).
            return q.status === 'fulfilled' && !q.data?.length && !!Number(t.length);
          })
          .map((t) => t.id);
        return gaps.length ? gaps : EMPTY_IDS;
      },
    [ahuId]
  );
  const sectionListGaps = useAppSelector(selectSectionListGaps, shallowEqual);

  // Bounded re-read, at most SECTION_LIST_RETRIES per tunnel per AHU.
  // `subscribe: false` keeps the fetch out of the subscription bookkeeping the
  // fan-out effect owns — the row still lands in the cache, which is what the
  // live selector reads, so a late success reaches the canvas through the
  // ordinary data-change path.
  //
  // The timers live in a ref keyed by tunnel id and are cancelled only when the
  // board switches AHU or unmounts: every new failure rewrites `sectionListGaps`,
  // and an effect cleanup that cancelled on each change would cancel SIBLING
  // tunnels' pending re-reads. For the same reason an attempt is counted when
  // its timer FIRES, not when it is scheduled, or a cancelled timer would spend
  // an attempt that never sent a request.
  //
  // A scheduled timer is a decision taken up to 1.8 s earlier, so the gap is
  // checked again when it fires: it can have closed on its own in the meantime
  // (the board's own post-delete `sections/?tunnel_id=` landing, or the tunnel
  // itself going), and the request is then neither needed nor answerable.
  // Re-read at fire time rather than cancelled on change, for the
  // sibling-tunnel reason above.
  const sectionRetryTimersRef = useRef<Record<number, number>>({});
  const sectionListGapsRef = useRef<number[]>(sectionListGaps);
  sectionListGapsRef.current = sectionListGaps;
  const [sectionRetryCounts, setSectionRetryCounts] = useState<Record<number, number>>({});
  useEffect(() => {
    const timers = sectionRetryTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => window.clearTimeout(t));
      sectionRetryTimersRef.current = {};
      setSectionRetryCounts({});
    };
  }, [ahuId]);
  useEffect(() => {
    sectionListGaps.forEach((id) => {
      if (sectionRetryTimersRef.current[id] != null) return; // one in flight for this tunnel
      const spent = sectionRetryCounts[id] ?? 0;
      if (spent >= SECTION_LIST_RETRIES) return;
      sectionRetryTimersRef.current[id] = window.setTimeout(() => {
        delete sectionRetryTimersRef.current[id];
        // Gone while we waited — no request, and no attempt spent on one.
        if (!sectionListGapsRef.current.includes(id)) return;
        setSectionRetryCounts((counts) => ({ ...counts, [id]: (counts[id] ?? 0) + 1 }));
        dispatch(
          ahuBoardApi.endpoints.getTunnelSectionList.initiate(id, {
            subscribe: false,
            forceRefetch: true,
          })
        );
      }, SECTION_LIST_RETRY_MS * (spent + 1));
    });
  }, [sectionListGaps, sectionRetryCounts, dispatch]);

  /**
   * The section data the canvas needs is really here — the FIRST-draw gate.
   *
   * `sectionsByTunnel != null` only says every list has SETTLED, and a settled
   * list can be an errored one degraded to `[]`, or a stale one a second tab's
   * insert left short. `AHUTunnels.length` is the live sum of the tunnel's
   * section lengths (models_tunnel.py), so a tunnel the server reports with a
   * length and no rows on this side is a half-loaded client, not an empty
   * tunnel — and drawing it paints a unit with sections missing (every section,
   * when the whole fan-out failed), which nothing would redraw.
   *
   * A tunnel stops being waited for once its re-reads above are spent, so a
   * genuinely unreachable list costs the board ~4s and then draws what there
   * is: never a board that will not draw at all. `useLatched` then holds the
   * answer for the rest of the session — see its note.
   */
  const sectionsComplete = useLatched(
    sectionsByTunnel != null &&
      (tunnels ?? []).every(
        (t) =>
          !Number(t.length) ||
          (sectionsByTunnel[t.id]?.length ?? 0) > 0 ||
          (sectionRetryCounts[t.id] ?? 0) >= SECTION_LIST_RETRIES
      ),
    ahuId
  );

  // ---- G29/G33 data: dmounts (§20.4 #38) + component meta (§20.5 #43→#47)
  // per section. The legacy fires the same per-section requests through the
  // tab.section layer at board load (dmountListener / component.collection);
  // hoisted here so the canvas never depends on the 6e tab mount order. ----
  // Dmounts are NOT snapshotted: like sectionsByTunnel above, the effect only
  // owns the fetch subscriptions and the DATA is read LIVE from the RTKQ cache
  // (selector below). A one-shot useState snapshot never saw later writes, so
  // adding a Split D-Mount from the sections tab (DmountGroup → refetchDmounts)
  // left the canvas drawing the pre-add rows until a page reload.
  const dmountSectionIds = useMemo(
    () =>
      sectionsByTunnel
        ? Object.values(sectionsByTunnel)
            .flat()
            .map((s) => s.id)
        : null,
    [sectionsByTunnel]
  );
  const selectDmountsBySection = useMemo(
    () =>
      (state: RootState): Record<number, IAhuDmountRow[]> | null => {
        if (!dmountSectionIds) return null;
        const acc: Record<number, IAhuDmountRow[]> = {};
        for (const id of dmountSectionIds) {
          const q = ahuBoardApi.endpoints.getSectionDmounts.select(id)(state);
          if (q.data) acc[id] = q.data;
          else if (q.isError) acc[id] = [];
          // null until every section's rows have landed — the old snapshot
          // semantics (legacy pre-listener state = no dmount dims yet).
          else return null;
        }
        return acc;
      },
    [dmountSectionIds]
  );
  const dmountsLive = useAppSelector(selectDmountsBySection, shallowEqual);
  // A freshly inserted section has no dmount rows yet, which nulls the map and
  // makes every section's `dmounts` null for a redraw. Hold the last map; the
  // new section reads as `[]` until its rows land.
  const dmountsBySection = useHeldValue(dmountsLive, ahuId);

  const [componentMetaBySection, setComponentMetaBySection] = useState<Record<
    number,
    SectionComponentMeta
  > | null>(null);
  const [coilFeedsBySection, setCoilFeedsBySection] = useState<Record<
    number,
    SectionCoilFeed
  > | null>(null);
  const [evapFeedsBySection, setEvapFeedsBySection] = useState<Record<
    number,
    IAhuEvapPackDetail
  > | null>(null);

  // Cleared when the board switches AHU only. Every section write re-runs the
  // fetch effect below, and nulling the feeds there made `canvasTunnels` drop
  // every coil duty / fan orientation / UDC flag and `bootDraws` vanish, so one
  // insert drew the canvas stripped and then again once the feeds came back.
  // The effect now replaces them in place when its fetches settle.
  useEffect(() => {
    setComponentMetaBySection(null);
    setCoilFeedsBySection(null);
    setEvapFeedsBySection(null);
    setIntWallsBySection(null);
    setAccessoriesBootBySection(null);
  }, [ahuId]);

  useEffect(() => {
    if (!sectionsByTunnel || holdNonCriticalFeeds) return undefined;
    const sectionIds = Object.values(sectionsByTunnel)
      .flat()
      .map((s) => s.id);
    if (!sectionIds.length) {
      setComponentMetaBySection({});
      setCoilFeedsBySection({});
      setEvapFeedsBySection({});
      return undefined;
    }
    let cancelled = false;
    const subs: Array<{ unsubscribe(): void }> = [];

    // §20.4 #38 — one GET per section (legacy request pattern). Subscription
    // only; selectDmountsBySection reads the rows straight out of the cache.
    subs.push(
      ...sectionIds.map((id) => dispatch(ahuBoardApi.endpoints.getSectionDmounts.initiate(id)))
    );

    // §20.5 #43 — components per section, then #47 concrete coil/fan/UDC/evap
    // models + #50 rated coils — the canvas-read fields (G33 duty/orientation,
    // label UDC probe) and the 6c2 G31/G32 piping feeds.
    const componentSubs = sectionIds.map((id) =>
      dispatch(ahuBoardApi.endpoints.getSectionComponents.initiate(id))
    );
    subs.push(...componentSubs);
    Promise.all(componentSubs.map((s) => s.unwrap().catch(() => [])))
      .then((componentLists) => {
        if (cancelled) return undefined;
        const detailPromises: Array<Promise<void>> = [];
        const acc: Record<number, SectionComponentMeta> = {};
        const coilAcc: Record<number, SectionCoilFeed> = {};
        const evapAcc: Record<number, IAhuEvapPackDetail> = {};
        componentLists.forEach((rows, i) => {
          const sectionId = sectionIds[i];
          acc[sectionId] = {};
          rows.forEach((row) => {
            if (row.type === 'coil') {
              // Retry once on failure: the legacy save-on-GET coil endpoint can
              // 500 transiently (thread-unsafe AMQP publish in syncdata, 6c2
              // verify finding 2); a silent drop loses this coil's piping for
              // the session, so retry and WARN if it still fails.
              const fetchCoil = () => {
                const s = dispatch(
                  ahuBoardApi.endpoints.getAhuCoilDetail.initiate(row.id, {
                    forceRefetch: true,
                  })
                );
                subs.push(s);
                return s.unwrap();
              };
              detailPromises.push(
                fetchCoil()
                  .catch(() => fetchCoil())
                  .then((coil) => {
                    acc[sectionId].coil_duty_id = coil.duty_id ?? null;
                    // G31 feed: the coil form fetches the RatedCoilModel at
                    // init and fires draw:coil:connection on resolve
                    // (coil/view/form.js:228-232).
                    const ratedSub = dispatch(
                      ahuBoardApi.endpoints.getAhuRatedCoil.initiate(coil.id)
                    );
                    subs.push(ratedSub);
                    return ratedSub
                      .unwrap()
                      .then((rated) => {
                        coilAcc[sectionId] = { coil, rated };
                      })
                      .catch(() => {
                        coilAcc[sectionId] = { coil, rated: null };
                      });
                  })
                  .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.warn(
                      `[ahu-board] coil ${row.id} detail failed twice — piping skipped`,
                      err
                    );
                  })
              );
            } else if (row.type === 'fan') {
              const sub = dispatch(ahuBoardApi.endpoints.getAhuFanDetail.initiate(row.id));
              subs.push(sub);
              detailPromises.push(
                sub
                  .unwrap()
                  .then((fan) => {
                    acc[sectionId].fan_orientation_id = fan.orientation_id ?? null;
                  })
                  .catch(() => undefined)
              );
            } else if (row.type === 'evap_pack') {
              // G32 feed (evap form drawConnections inputs incl. the
              // server-joined cms_fill/cms_drain/cms_overflow blocks).
              const sub = dispatch(ahuBoardApi.endpoints.getAhuEvapPackDetail.initiate(row.id));
              subs.push(sub);
              detailPromises.push(
                sub
                  .unwrap()
                  .then((evap) => {
                    evapAcc[sectionId] = evap;
                  })
                  .catch(() => undefined)
              );
            } else if (row.type === 'uds' || row.type === 'user_defined_component') {
              // getSectionNameColor UDC probe (canvas.view3d:5739).
              const sub = dispatch(ahuBoardApi.endpoints.getAhuUdcDetail.initiate(row.id));
              subs.push(sub);
              detailPromises.push(
                sub
                  .unwrap()
                  .then((udc) => {
                    acc[sectionId].udc_has_name = !!udc.udc_name_id;
                  })
                  .catch(() => undefined)
              );
            } else if (row.type === 'gas_heater') {
              // getSectionNameColor gas-heater probe. forceRefetch like the
              // coil branch: this effect re-runs off the section refetch that
              // the selector's Select queues, and a cached detail would still
              // carry the pre-Select `model`.
              const sub = dispatch(
                ahuBoardApi.endpoints.getAhuGasHeaterDetail.initiate(row.id, {
                  forceRefetch: true,
                })
              );
              subs.push(sub);
              detailPromises.push(
                sub
                  .unwrap()
                  .then((gh) => {
                    acc[sectionId].gh_has_model = !!gh.model;
                  })
                  .catch(() => undefined)
              );
            }
            // energy_wheel: the legacy config probe reads
            // `model.view.catalogueParams` — a selector-runtime stamp that is
            // ALSO absent at legacy boot, so the `next_to ? 2 : 1` fallback is
            // boot-parity (canvas.view3d:5437); no wheel fetch here.
          });
        });
        return Promise.all(detailPromises).then(() => {
          if (cancelled) return;
          setComponentMetaBySection(acc);
          setCoilFeedsBySection(coilAcc);
          setEvapFeedsBySection(evapAcc);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setComponentMetaBySection({});
          setCoilFeedsBySection({});
          setEvapFeedsBySection({});
        }
      });

    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [sectionsByTunnel, holdNonCriticalFeeds, dispatch]);

  // ---- 6c2: int-walls + drawable accessories per section (G11/G12-G21 boot
  // inputs — legacy: tab.section int-wall collections + every accessory
  // form's boot fetch chain model+position, §49.6). ----
  const [intWallsBySection, setIntWallsBySection] = useState<Record<
    number,
    IAhuIntWallRow[]
  > | null>(null);
  const [accessoriesBootBySection, setAccessoriesBootBySection] = useState<Record<
    number,
    AccessoryBootRow[]
  > | null>(null);

  useEffect(() => {
    if (!sectionsByTunnel || holdNonCriticalFeeds) return undefined;
    const sectionIds = Object.values(sectionsByTunnel)
      .flat()
      .map((s) => s.id);
    if (!sectionIds.length) {
      setIntWallsBySection({});
      setAccessoriesBootBySection({});
      return undefined;
    }
    let cancelled = false;
    const subs: Array<{ unsubscribe(): void }> = [];

    // §20.4 #31 + #34 — both planes per section, plane stamped by the api layer.
    const wallPromises = sectionIds.map((id) => {
      const v = dispatch(ahuBoardApi.endpoints.getSectionVerticalIntWalls.initiate(id));
      const h = dispatch(ahuBoardApi.endpoints.getSectionHorizontalIntWalls.initiate(id));
      subs.push(v, h);
      return Promise.all([
        v.unwrap().catch(() => [] as IAhuIntWallRow[]),
        h.unwrap().catch(() => [] as IAhuIntWallRow[]),
      ]).then(([vRows, hRows]) => [...vRows, ...hRows]);
    });
    Promise.all(wallPromises)
      .then((results) => {
        if (cancelled) return;
        const acc: Record<number, IAhuIntWallRow[]> = {};
        results.forEach((rows, i) => {
          acc[sectionIds[i]] = rows;
        });
        setIntWallsBySection(acc);
      })
      .catch(() => {
        if (!cancelled) setIntWallsBySection({});
      });

    // §20.6 #51 rows → #54 concrete detail + #59 position for the drawable
    // types (the same per-accessory fetch pair every legacy form fires).
    const accessorySubs = sectionIds.map((id) =>
      dispatch(ahuBoardApi.endpoints.getSectionAccessories.initiate(id))
    );
    subs.push(...accessorySubs);
    Promise.all(accessorySubs.map((s) => s.unwrap().catch(() => [])))
      .then((lists) => {
        if (cancelled) return undefined;
        const acc: Record<number, AccessoryBootRow[]> = {};
        const detailPromises: Array<Promise<void>> = [];
        lists.forEach((rows, i) => {
          const sectionId = sectionIds[i];
          acc[sectionId] = [];
          (rows as IAhuAccessoryRow[]).forEach((row) => {
            const type = String(row.type || '');
            const plural = DRAWABLE_ACCESSORY_TYPES[type];
            if (!plural) return; // starter/uvc/uda/ams/floor_drain — never drawn (no shapes map entry)
            const bootRow: AccessoryBootRow = { type, row, detail: null, position: null };
            acc[sectionId].push(bootRow);
            const detailSub = dispatch(
              ahuBoardApi.endpoints.getAccessoryDetail.initiate({ plural, id: row.id })
            );
            subs.push(detailSub);
            detailPromises.push(
              detailSub
                .unwrap()
                .then((detail) => {
                  bootRow.detail = detail;
                })
                .catch(() => undefined)
            );
            if (accessoryTypeNeedsPosition(type)) {
              const positionSub = dispatch(
                ahuBoardApi.endpoints.getAccessoryPosition.initiate(row.id)
              );
              subs.push(positionSub);
              detailPromises.push(
                positionSub
                  .unwrap()
                  .then((position) => {
                    bootRow.position = position;
                  })
                  .catch(() => undefined)
              );
            }
          });
        });
        return Promise.all(detailPromises).then(() => {
          if (!cancelled) setAccessoriesBootBySection(acc);
        });
      })
      .catch(() => {
        if (!cancelled) setAccessoriesBootBySection({});
      });

    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [sectionsByTunnel, holdNonCriticalFeeds, dispatch]);

  // ---- 6c2: lug placements + floor labels per tunnel (G7-G10 LugCollection
  // re-home — tab.lifting.lug init fetches one tunnel_lugs model per base_id
  // tunnel then fires redraw:canvas; floor probe for isCube plane 1). ----
  const [lugsByTunnel, setLugsByTunnel] = useState<Record<
    number,
    IAhuTunnelLugPlacement
  > | null>(null);
  const [floorLabelByTunnel, setFloorLabelByTunnel] = useState<Record<
    number,
    unknown
  > | null>(null);

  useEffect(() => {
    setLugsByTunnel(null);
    setFloorLabelByTunnel(null);
    if (!tunnels || holdNonCriticalFeeds) return undefined;
    let cancelled = false;
    const subs: Array<{ unsubscribe(): void }> = [];

    // §20.7 #63 — `tunnels.toJSON().filter((t) => t.base_id)` (tab.lifting.lug:93),
    // narrowed to tunnels that actually carry lugs: this feed exists only for the
    // canvas, and drawLugs/redrawTunnelLugs bail on the same flag
    // (canvas.view3d:1991), so fetching a lug-less tunnel is a guaranteed-unused
    // GET — and a 404 whenever the tunnel has no AHULug row. The flag has to go
    // through `lugsOnForTunnel`: on a hybrid AHU it lives on the unit and every
    // tunnel's own `lug` stays false, so filtering on `t.lug` fetched NO
    // placement for a hybrid AHU at all — no lugs on its canvas, none in its pane.
    const lugTunnels = tunnels.filter((t) => t.base_id && lugsOnForTunnel(ahu, t));
    const lugAcc: Record<number, IAhuTunnelLugPlacement> = {};
    const lugPromises = lugTunnels.map((t) => {
      const sub = dispatch(ahuBoardApi.endpoints.getTunnelLugPlacement.initiate(t.id));
      subs.push(sub);
      return sub
        .unwrap()
        .then((placement) => {
          lugAcc[t.id] = placement;
        })
        .catch(() => undefined);
    });
    Promise.all(lugPromises).then(() => {
      if (!cancelled) setLugsByTunnel(lugAcc);
    });

    // ahu/floors/<floor_id> — under_insulation_label probe.
    const floorAcc: Record<number, unknown> = {};
    const floorPromises = tunnels
      .filter((t) => t.floor_id)
      .map((t) => {
        const sub = dispatch(ahuBoardApi.endpoints.getAhuFloorDetail.initiate(Number(t.floor_id)));
        subs.push(sub);
        return sub
          .unwrap()
          .then((floor) => {
            floorAcc[t.id] = floor.under_insulation_label;
          })
          .catch(() => undefined);
      });
    Promise.all(floorPromises).then(() => {
      if (!cancelled) setFloorLabelByTunnel(floorAcc);
    });

    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
    // The two AHU-level lug flags, not the whole `ahu` object: they are all
    // `lugsOnForTunnel` reads here, and depending on the object would re-run
    // every placement and floor GET on any unrelated AHU PATCH.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnels, holdNonCriticalFeeds, dispatch, ahu?.is_hybrid, ahu?.lug]);

  const permissions = permissionsList?.[0];

  return {
    user,
    ahu,
    tunnels,
    sectionsByTunnel,
    dmountsBySection,
    componentMetaBySection,
    coilFeedsBySection,
    evapFeedsBySection,
    intWallsBySection,
    accessoriesBootBySection,
    lugsByTunnel,
    floorLabelByTunnel,
    project,
    permissions,
    constructionClasses,
    cms,
    shellReady: !!ahu && !!tunnels,
    sectionsComplete,
    isHybridUnit,
    ahuError,
  };
}
